"use client";
import { createContext, PropsWithChildren, useContext, useState } from "react";
import { Locale, useLocale } from "../locale";
import { FLIGHT_STATIC_DATA_MOCK } from "./mocks";
import { StaticData } from "./types";

type FlightStaticContextType = {
  data: StaticData;
  isLoading?: boolean;
  isError: boolean;
  refetch: () => void;
};

const FlightStaticContext = createContext<FlightStaticContextType>({
  data: undefined!,
  isLoading: false,
  isError: false,
  refetch: () => {
    throw new Error("FlightStaticContext not initialized");
  },
});

FlightStaticContext.displayName = "FlightStaticContext";

type Props = Omit<FlightStaticContextType, "refetch" | "isLoading"> & {
  getFlightStaticData: (locale: Locale) => Promise<{
    data: StaticData;
    error: boolean;
  }>;
};

export default function FlightStaticProvider({
  data: initialData,
  isError: initialError = false,
  children,
  getFlightStaticData,
}: PropsWithChildren<Props>) {
  const { locale } = useLocale();
  const [data, setData] = useState<StaticData>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(initialError);

  function refetch() {
    setIsLoading(true);
    getFlightStaticData(locale || Locale.ENID)
      .then((response) => {
        const { data, error } = response;
        setData(data);
        setIsError(error);
      })
      .finally(() => setIsLoading(false));
  }

  return (
    <FlightStaticContext.Provider value={{ data, isLoading, isError, refetch }}>
      {children}
    </FlightStaticContext.Provider>
  );
}

export function useFlightStaticData(isMock = false) {
  const context = useContext(FlightStaticContext);

  if (!context) {
    throw new Error(
      "useFlightStaticData must be used within a FlightStaticProvider"
    );
  }

  if (isMock) {
    return {
      data: FLIGHT_STATIC_DATA_MOCK,
      isLoading: false,
      isError: false,
      refetch: () => {},
    };
  }

  return context;
}
